/**
 * Filename validation utility
 * Rules:
 * - No emojis
 * - No OS-invalid characters
 * - Allows: letters, numbers, space, _ - . ( )
 * - Prevents empty or whitespace-only names
 */

const EMOJI_REGEX = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;

const INVALID_CHAR_REGEX = /[<>:"/\\|?*\x00-\x1F]/;

const VALID_FILENAME_REGEX = /^[a-zA-Z0-9 _.\-()]+$/;

export const truncate = (filename, max = 15) => {
  if (!filename || filename.length <= max) return filename;

  const lastDotIndex = filename.lastIndexOf(".");

  // No extension → normal truncate
  if (lastDotIndex === -1) {
    return filename.slice(0, max) + "…";
  }

  const name = filename.slice(0, lastDotIndex);
  const ext = filename.slice(lastDotIndex); // includes "."

  // Reserve space for extension + ellipsis
  const available = max - ext.length - 1; // 1 for "…"

  if (available <= 0) {
    // Edge case: extension itself is too long
    return "…" + ext.slice(0, max - 1);
  }

  return name.slice(0, available) + "…" + ext;
};

export const validateFileName = (fileName, maxLength = 100) => {
  if (!fileName || typeof fileName !== "string") {
    return { valid: false, reason: "Filename is required." };
  }

  const trimmed = fileName.trim();

  if (!trimmed) {
    return { valid: false, reason: "Filename cannot be empty." };
  }

  // 🔒 Length check
  if (trimmed.length > maxLength) {
    return {
      valid: false,
      // reason: `Filename too long. Maximum ${maxLength} characters allowed.`,
      reason: `Filename too long. Skipped `,
    };
  }

  // Split extension safely
  const parts = trimmed.split(".");
  const nameWithoutExt =
    parts.length > 1 ? parts.slice(0, -1).join(".") : trimmed;

  if (!nameWithoutExt.trim()) {
    return { valid: false, reason: "Filename is invalid." };
  }

  // 🚫 Emoji check
  if (EMOJI_REGEX.test(trimmed)) {
    return { valid: false, reason: "Emojis are not allowed. Skipped " };
  }

  // 🚫 OS-invalid characters
  if (INVALID_CHAR_REGEX.test(trimmed)) {
    return {
      valid: false,
      reason: 'Filename contains invalid characters: <>:"/\\|?*',
    };
  }

  // ✅ Allowed pattern
  if (!VALID_FILENAME_REGEX.test(nameWithoutExt)) {
    return {
      valid: false,
      reason: "Special character not allowed. Skipped ",
    };
  }

  return { valid: true };
};

export const sanitizeFileName = (fileName) => {
  return fileName
    .replace(EMOJI_REGEX, "")
    .replace(INVALID_CHAR_REGEX, "")
    .replace(/\s+/g, " ")
    .trim();
};
